import { useQuery } from "@tanstack/react-query";
import { Button, Col, Divider, Dropdown, Flex, message, Row, Select, Space, Tabs, TabsProps, theme } from "antd";
import Typography from "antd/es/typography/Typography";
import Calendar from "components/calendar/calendar";
import { useBoolean } from "hooks/use-boolean";
import { add, get } from "lodash";
import moment from "moment";
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { MdDownload } from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import axiosInstance, { endpoints } from "services/axios";
import { getYearOptionsForAntSelect } from "utils/functions";

interface IProps {
  scheduleData: any;
}

const Holidays = forwardRef(({ scheduleData }: IProps, ref) => {
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get("token");
  const [year, setYear] = useState<number>(moment().get("year"));
  const [quarter, setQuarter] = useState<number>(0);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [importScheduleId, setImportScheduleId] = useState<string | null>(null);
  const importDropDownBool = useBoolean();

  const { data: schedulesData } = useQuery({
    queryKey: ["schedules-list"],
    queryFn: async () => {
      axiosInstance.defaults.headers.common.Authorization = accessToken;
      return await axiosInstance.get(endpoints.schedule.list);
    },
  });

  useImperativeHandle(ref, () => ({
    getData() {
      return selectedDates;
    },
  }));

  const { token } = theme.useToken();

  const contentStyle: React.CSSProperties = {
    backgroundColor: token.colorBgElevated,
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadowSecondary,
    padding: token.padding,
  };

  const tabItems: TabsProps["items"] = [
    {
      key: "0",
      label: "Quarter 1",
      children: <></>,
    },
    {
      key: "1",
      label: "Quarter 2",
      children: <></>,
    },
    {
      key: "2",
      label: "Quarter 3",
      children: <></>,
    },
    {
      key: "3",
      label: "Quarter 4",
      children: <></>,
    },
  ];

  const handleImportScheduleHolidays = useCallback(() => {
    importDropDownBool.onTrue();
    const schedule = get(schedulesData, "data.data", []).find((_: any) => _.id === importScheduleId);
    if (!schedule) return;

    setSelectedDates((dates) => [...dates, ...get(schedule, "holidays", [])]);
    message.success("Holidays successfully  imported!");
    importDropDownBool.onFalse();
    setImportScheduleId(null);
  }, [importScheduleId, importDropDownBool, schedulesData]);

  useEffect(() => {
    setQuarter(moment().quarter() - 1);
    if (get(scheduleData, "holidays")) setSelectedDates(get(scheduleData, "holidays"));
  }, [scheduleData]);

  return (
    <>
      <Flex justify="space-between">
        <Space className="my-3">
          <Typography>Holidays for </Typography>
          <Select value={year} onChange={setYear} options={getYearOptionsForAntSelect(4, 4)} />
        </Space>
        <Dropdown
          placement="bottomRight"
          open={importDropDownBool.value}
          onOpenChange={(val) => !val && importDropDownBool.onFalse()}
          dropdownRender={() => (
            <div style={contentStyle}>
              <Select value={importScheduleId} onChange={setImportScheduleId} className="w-full" placeholder="Select a schedule">
                {get(schedulesData, "data.data", []).map((item: any) => (
                  <Select.Option value={item.id} key={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
              <Divider className="my-3" />
              <Space className="justify-end w-full">
                <Button onClick={handleImportScheduleHolidays} type="primary" icon={<MdDownload />} iconPosition="end">
                  Import
                </Button>
              </Space>
            </div>
          )}
          trigger={["click"]}
        >
          <Button onClick={importDropDownBool.onTrue} type="dashed" icon={<MdDownload />}>
            Import holidays from...
          </Button>
        </Dropdown>
      </Flex>
      <Tabs activeKey={quarter.toString()} onChange={(val: string) => setQuarter(Number(val))} items={tabItems} />

      <Row gutter={[10, 10]}>
        <Col className="flex flex-col justify-start " xs={24} sm={8} lg={6}>
          <Calendar selectedDates={selectedDates} setSelectedDates={setSelectedDates} year={year} month={add(quarter * 3, 0)} />
        </Col>
        <Col className="flex flex-col justify-start" xs={24} sm={8} lg={6}>
          <Calendar selectedDates={selectedDates} setSelectedDates={setSelectedDates} year={year} month={add(quarter * 3, 1)} />
        </Col>
        <Col className="flex flex-col justify-start" xs={24} sm={8} lg={6}>
          <Calendar selectedDates={selectedDates} setSelectedDates={setSelectedDates} year={year} month={add(quarter * 3, 2)} />
        </Col>
      </Row>
    </>
  );
});

export default memo(Holidays);
